import { LitElement, html } from 'lit';
import { classMap } from 'lit/directives/class-map.js';

import componentStyles from './fil-switch-group.styles.js';

/**
 * @tag fil-switch-group
 * @property {string}[checked] Value of the checked switches of the component
 * @property {string}[name] Name of the component
 * @property {'standard'|'small'}[size] Size of the component
 * @fires change.gds.switch (event: Event) => void
 */
export class FilSwitchGroup extends LitElement {
  static properties = {
    checked: { type: String },
    name: { type: String },
    size: { type: String }, // 'small' | 'standard'
  };

  static styles = [componentStyles];

  get slottedChildren() {
    return this.shadowRoot.querySelector('slot').assignedElements({ flatten: true });
  }

  constructor() {
    super();
    this.checked = null;
    this.size = 'standard';
  }

  // will be called if a switch in the same group changes its state to 'checked'.
  // it will remove the 'checked' state for all others.
  onChangeGdsSwitch(event) {
    if (event.target === this) {
      return;
    }

    const { detail } = event;

    // ignore if it's the same one
    if (this.checked === detail.value) {
      return;
    }

    this.checked = detail.value;

    // change state on all children accordingly
    this.slottedChildren.forEach(switchItem => {
      if (this.checked === switchItem.value) {
        switchItem.setAttribute('checked', '');
      } else {
        switchItem.removeAttribute('checked');
      }
    });
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change.gds.switch', this.onChangeGdsSwitch);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change.gds.switch', this.onChangeGdsSwitch);
  }

  firstUpdated(changes) {
    super.firstUpdated(changes);

    this.slottedChildren.forEach(switchItem => {
      switchItem.setAttribute('name', this.name);

      if (this.checked !== null) {
        if (this.checked === switchItem.value) {
          switchItem.setAttribute('checked', '');
        } else {
          switchItem.removeAttribute('checked');
        }
      }
    });
  }

  updated(changedProperties) {
    super.updated(changedProperties);

    if (changedProperties.has('size')) {
      this.slottedChildren.forEach(switchItem => {
        if (this.size) {
          switchItem.setAttribute('size', this.size);
        } else {
          switchItem.removeAttribute('size');
        }
      });
    }

    if (this.checked !== null && changedProperties.has('checked')) {
      this.slottedChildren.forEach(switchItem => {
        if (this.checked === switchItem.value) {
          switchItem.setAttribute('checked', '');
        } else {
          switchItem.removeAttribute('checked');
        }
      });
    }
  }

  render() {
    const classes = {
      [`fil-switch-group--${this.size}`]: this.size !== 'standard',
    };

    return html`<div
      class="fil-switch-group${classMap(classes)}"
      role="radiogroup"
      aria-label="Switch Group"
    >
      <slot></slot>
    </div>`;
  }
}

export default FilSwitchGroup;
